import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-800 mb-3">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.category);
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '黑料社',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.isExclusive && (
                <span className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
                  独家爆料
                </span>
              )}
              {article.isHot && (
                <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              <Link
                to={`/category/${article.category}`}
                className="text-xs text-foreground-400 hover:text-primary-500 transition-colors"
              >
                {article.categoryName}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 text-sm text-foreground-400 mb-6 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.readCount.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()} 评论
              </span>
            </div>

            <div className="rounded-xl overflow-hidden bg-background-200 mb-8 aspect-[16/8]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-foreground-100/60 mt-8 pt-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-6 border-t border-foreground-100/60">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                  title="点赞"
                >
                  <i className="ri-thumb-up-line text-lg"></i>
                  <span>3.2k</span>
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                  title="收藏"
                >
                  <i className="ri-bookmark-line text-lg"></i>
                  <span>收藏</span>
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                  title="分享"
                >
                  <i className="ri-share-forward-line text-lg"></i>
                  <span>分享</span>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/60 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-links-line text-secondary-600 text-lg"></i>
                </div>
                <h2 className="text-xl font-heading text-foreground-900">相关爆料</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}